import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  Plus,
  Loader2,
  AlertCircle,
  Search,
  Eye,
  Trash2,
  Clock,
  Bell,
  Database,
  GripVertical,
  X,
  Building2,
  Users,
  Tag,
  Filter,
  CheckCircle2,
  Link2,
  FileDown,
} from "lucide-react";
import api, { extractError } from "../lib/api";
import { renderRichText } from "../lib/richText";
import AiBotWidget from "../components/ai-bot/AiBotWidget";
import type {
  Questionnaire,
  PaginatedResponse,
  Organization,
  DataField,
  Cluster,
  ClusterAssignment,
  AfterSubmitAction,
} from "../types";
import { QuestionnaireStatus } from "../types";

const afterSubmitActionLabels: Record<AfterSubmitAction, string> = {
  SHOW_READONLY: "Antworten unveränderbar anzeigen",
  REDIRECT: "Auf Ziel-URL weiterleiten",
  REFILL: "Formular erneut ausfüllen",
  THANKS_TEXT: "Dankestext anzeigen",
};

const statusLabels: Record<QuestionnaireStatus, string> = {
  [QuestionnaireStatus.Draft]: "Entwurf",
  [QuestionnaireStatus.Active]: "Aktiv",
  [QuestionnaireStatus.Archived]: "Archiviert",
};

const statusColors: Record<QuestionnaireStatus, string> = {
  [QuestionnaireStatus.Draft]: "badge-warning",
  [QuestionnaireStatus.Active]: "badge-success",
  [QuestionnaireStatus.Archived]: "badge-danger",
};

type DeadlineType = "once" | "daily" | "cron";

const deadlineTypeLabels: Record<DeadlineType, string> = {
  once: "Einmalig",
  daily: "Täglich",
  cron: "Cron-Ausdruck",
};

interface QuestionnaireFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function QuestionnaireFormModal({
  isOpen,
  onClose,
}: QuestionnaireFormModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    organizationId: "",
    status: QuestionnaireStatus.Draft,
    deadlineType: "once" as DeadlineType,
    deadline: "",
    cronExpression: "",
    submissionLimit: "",
    remindBeforeSeconds: "",
    reminderActive: false,
    allowEditWithinDay: false,
    afterSubmitAction: "REFILL" as AfterSubmitAction,
    pdfEnabled: false,
    redirectUrl: "",
    thanksText: "",
    fieldSelections: [] as { fieldId: string; label: string }[],
    assigneeUserIds: [] as string[],
    assigneeOrgIds: [] as string[],
    clusterIds: [] as string[],
  });
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Native selects können Optionen nicht einfärben – der Punkt neben dem Select
  // zeigt daher die Farbe des zuletzt gewählten Clusters (die Auswahl wird
  // direkt als Chip übernommen und das Select zurückgesetzt).
  const [lastClusterPickId, setLastClusterPickId] = useState("");

  const { data: orgsData } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ["organizations"],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Organization>>("/organizations", { params: { limit: 200 } });
      return res.data;
    },
  });

  const { data: usersData } = useQuery<any[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await api.get("/users", { params: { limit: 500 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });

  const { data: fieldsData } = useQuery<PaginatedResponse<DataField>>({
    queryKey: ["fields"],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<DataField>>("/data-fields", { params: { limit: 500 } });
      return res.data;
    },
  });

  const { data: clustersData } = useQuery<Cluster[]>({
    queryKey: ["clusters"],
    queryFn: async () => {
      const res = await api.get("/clusters");
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });
  const clusters = clustersData ?? [];

  // Resolve fields
  const allFields: DataField[] = Array.isArray(fieldsData)
    ? fieldsData
    : fieldsData?.data ?? [];
  
  const organizations: Organization[] = Array.isArray(orgsData)
    ? orgsData
    : orgsData?.data ?? [];

  const users = Array.isArray(usersData) ? usersData : (usersData?.data ?? []);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        description: data.description,
        organizationId: data.organizationId,
        status: data.status,
        deadlineType: data.deadlineType,
        deadline: null,
        cronExpression: null,
        submissionLimit: null,
        remindBeforeSeconds: null,
        assigneeUserIds: data.assigneeUserIds.length > 0 ? data.assigneeUserIds : undefined,
        assigneeOrgIds: data.assigneeOrgIds.length > 0 ? data.assigneeOrgIds : undefined,
        allowEditWithinDay: data.allowEditWithinDay,
        clusterIds: data.clusterIds.length > 0 ? data.clusterIds : undefined,
        afterSubmitAction: data.afterSubmitAction,
        pdfEnabled: data.afterSubmitAction === "SHOW_READONLY" ? data.pdfEnabled : false,
        redirectUrl: data.afterSubmitAction === "REDIRECT" ? (data.redirectUrl.trim() || null) : null,
        thanksText: data.afterSubmitAction === "THANKS_TEXT" ? (data.thanksText.trim() || null) : null,
      };

      if (data.deadlineType === "once") {
        payload.deadline = data.deadline ? new Date(data.deadline).toISOString() : null;
      } else if (data.deadlineType === "daily") {
        // For daily deadlines: use today at given time, if already past use tomorrow
        payload.deadline = data.deadline
          ? (() => {
              const d = new Date(new Date().toDateString() + " " + data.deadline);
              if (d <= new Date()) d.setDate(d.getDate() + 1);
              return d.toISOString();
            })()
          : null;
      } else if (data.deadlineType === "cron") {
        payload.cronExpression = data.cronExpression || null;
      }

      if (data.submissionLimit !== "") {
        payload.submissionLimit = parseInt(data.submissionLimit, 10);
      }

      if (data.reminderActive && data.remindBeforeSeconds !== "") {
        payload.remindBeforeSeconds = parseInt(data.remindBeforeSeconds, 10);
      }

      // Add field selections with labels
      if (formData.fieldSelections.length > 0) {
        payload.fields = formData.fieldSelections.map(f => ({
          fieldId: f.fieldId,
          label: f.label || undefined,
        }));
      }

      const res = await api.post("/questionnaires", payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionnaires"] });
      if (typeof onClose === "function") onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  const addField = (fieldId: string) => {
    if (formData.fieldSelections.some(f => f.fieldId === fieldId)) return;
    const field = allFields.find(f => f.id === fieldId);
    setFormData(prev => ({
      ...prev,
      fieldSelections: [...prev.fieldSelections, { fieldId, label: field?.label || field?.key || field?.name || "" }],
    }));
  };

  const removeField = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      fieldSelections: prev.fieldSelections.filter(f => f.fieldId !== fieldId),
    }));
  };

  const updateFieldLabel = (fieldId: string, label: string) => {
    setFormData(prev => ({
      ...prev,
      fieldSelections: prev.fieldSelections.map(f =>
        f.fieldId === fieldId ? { ...f, label } : f
      ),
    }));
  };

  const availableFields = allFields.filter(
    f => !formData.fieldSelections.some(s => s.fieldId === f.id)
  );

  const addAssigneeUser = (userId: string) => {
    if (formData.assigneeUserIds.includes(userId)) return;
    setFormData(prev => ({ ...prev, assigneeUserIds: [...prev.assigneeUserIds, userId] }));
  };

  const removeAssigneeUser = (userId: string) => {
    setFormData(prev => ({ ...prev, assigneeUserIds: prev.assigneeUserIds.filter(id => id !== userId) }));
  };

  const addAssigneeOrg = (orgId: string) => {
    if (formData.assigneeOrgIds.includes(orgId)) return;
    setFormData(prev => ({ ...prev, assigneeOrgIds: [...prev.assigneeOrgIds, orgId] }));
  };

  const removeAssigneeOrg = (orgId: string) => {
    setFormData(prev => ({ ...prev, assigneeOrgIds: prev.assigneeOrgIds.filter(id => id !== orgId) }));
  };
  const addCluster = (clusterId: string) => {
    if (formData.clusterIds.includes(clusterId)) return;
    setFormData(prev => ({ ...prev, clusterIds: [...prev.clusterIds, clusterId] }));
  };
  const removeCluster = (clusterId: string) => {
    setFormData(prev => ({ ...prev, clusterIds: prev.clusterIds.filter(id => id !== clusterId) }));
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Neuer Fragebogen
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              className="input-field"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          {/* Description */}
          <div>
            <label className="label">Beschreibung</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* Organization */}
          <div>
            <label className="label">Organisation (erstellende OE)</label>
            <select
              className="input-field"
              required
              value={formData.organizationId}
              onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
            >
              <option value="">Bitte wählen</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="label">Status</label>
            <select
              className="input-field"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as QuestionnaireStatus })}
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* allowEditWithinDay */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={formData.allowEditWithinDay}
                onChange={(e) => setFormData({ ...formData, allowEditWithinDay: e.target.checked })}
              />
              <span className="text-sm text-gray-700">Nutzern erlauben, Einreichungen am selben Tag zu korrigieren</span>
            </label>
            <p className="text-xs text-gray-400 mt-1 ml-6">Bei Aktivierung erscheinen heutige Einreichungen unter „Meine Fragebögen" zur Korrektur.</p>
          </div>

          {/* ─── Verhalten nach dem Absenden ─── */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <CheckCircle2 size={16} /> Nach dem Absenden
            </h3>

            <div>
              <label className="label">Aktion</label>
              <select
                className="input-field"
                value={formData.afterSubmitAction}
                onChange={(e) => setFormData({ ...formData, afterSubmitAction: e.target.value as AfterSubmitAction })}
              >
                {(Object.entries(afterSubmitActionLabels) as [AfterSubmitAction, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {formData.afterSubmitAction === "SHOW_READONLY" && (
              <div className="mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={formData.pdfEnabled}
                    onChange={(e) => setFormData({ ...formData, pdfEnabled: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    <FileDown size={14} /> PDF-Download erlauben
                  </span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-6">Der Ausfüllende kann seine eingegebenen Daten als PDF herunterladen.</p>
              </div>
            )}

            {formData.afterSubmitAction === "REDIRECT" && (
              <div className="mt-3">
                <label className="label flex items-center gap-1">
                  <Link2 size={14} /> Ziel-URL
                </label>
                <input
                  type="url"
                  className="input-field"
                  required
                  placeholder="https://example.org/danke"
                  value={formData.redirectUrl}
                  onChange={(e) => setFormData({ ...formData, redirectUrl: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">Nach dem Absenden wird auf diese Adresse weitergeleitet (http/https).</p>
              </div>
            )}

            {formData.afterSubmitAction === "THANKS_TEXT" && (
              <div className="mt-3">
                <label className="label">Dankestext</label>
                <textarea
                  className="input-field"
                  rows={4}
                  required
                  placeholder={"z. B. Vielen Dank für die Einreichung!\n\n**Wichtig:** …"}
                  value={formData.thanksText}
                  onChange={(e) => setFormData({ ...formData, thanksText: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">HTML oder Markdown möglich. Die Vorschau zeigt die spätere Anzeige.</p>
                {formData.thanksText.trim() && (
                  <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <p className="text-xs text-gray-400 mb-2">Vorschau</p>
                    <div
                      className="space-y-1 [&_a]:text-primary [&_a]:underline [&_h2]:font-semibold [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: renderRichText(formData.thanksText) }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── Deadline section ─── */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <Clock size={16} /> Frist & Wiederholung
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Frist-Typ</label>
                <select
                  className="input-field"
                  value={formData.deadlineType}
                  onChange={(e) => setFormData({ ...formData, deadlineType: e.target.value as DeadlineType })}
                >
                  {(Object.entries(deadlineTypeLabels) as [DeadlineType, string][]).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                {formData.deadlineType === "once" && (
                  <>
                    <label className="label">Fällig am</label>
                    <input
                      type="datetime-local"
                      className="input-field"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    />
                  </>
                )}
                {formData.deadlineType === "daily" && (
                  <>
                    <label className="label">Fällig um (täglich)</label>
                    <input
                      type="time"
                      className="input-field"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    />
                  </>
                )}
                {formData.deadlineType === "cron" && (
                  <>
                    <label className="label">Cron-Ausdruck</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="z. B. 0 8 * * *"
                      value={formData.cronExpression}
                      onChange={(e) => setFormData({ ...formData, cronExpression: e.target.value })}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="label">Einreichungslimit (leer = unbegrenzt, 1 = einmal pro Nutzer)</label>
              <input
                type="number"
                min="1"
                className="input-field"
                placeholder="Z. B. 1 für einmalige Einreichung"
                value={formData.submissionLimit}
                onChange={(e) => setFormData({ ...formData, submissionLimit: e.target.value })}
              />
            </div>
          </div>

          {/* ─── Data Fields section ─── */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <Database size={16} /> Datenfelder
            </h3>

            {formData.fieldSelections.length === 0 ? (
              <p className="text-sm text-gray-400 mb-3">
                Keine Datenfelder ausgewählt. Wählen Sie Felder aus, die im Fragebogen ausgefüllt werden sollen.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {formData.fieldSelections.map((sel) => {
                  const field = allFields.find(f => f.id === sel.fieldId);
                  return (
                    <div key={sel.fieldId} className="flex items-start gap-2 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <GripVertical size={16} className="text-gray-300 mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{field?.label || field?.key || sel.fieldId}</span>
                          <span className="badge badge-info text-[10px]">{field?.type || "?"}</span>
                        </div>
                        <input
                          type="text"
                          className="mt-1 input-field text-sm"
                          placeholder="Anzeigetext / Beschreibung für dieses Feld"
                          value={sel.label}
                          onChange={(e) => updateFieldLabel(sel.fieldId, e.target.value)}
                        />
                      </div>
                      <button type="button" onClick={() => removeField(sel.fieldId)} className="btn-ghost p-1 text-gray-400 hover:text-red-500">
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {availableFields.length > 0 ? (
              <div className="relative">
                <button type="button" onClick={() => setShowFieldPicker(!showFieldPicker)} className="btn-secondary text-sm">
                  <Plus size={14} /> Datenfeld hinzufügen
                </button>
                {showFieldPicker && (
                  <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {availableFields.map((field) => (
                      <button
                        key={field.id}
                        type="button"
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                        onClick={() => { addField(field.id); setShowFieldPicker(false); }}
                      >
                        <span className="font-medium">{field.label || field.key || field.name}</span>
                        <span className="badge badge-info text-[10px]">{field.type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">Alle verfügbaren Datenfelder sind bereits ausgewählt.</p>
            )}
          </div>

          {/* ─── Cluster section ─── */}
          <div>
            <label className="label flex items-center gap-1">
              <Tag size={14} /> Cluster (Pakete)
            </label>
            {formData.clusterIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.clusterIds.map(clusterId => {
                  const cluster = clusters.find(c => c.id === clusterId);
                  return (
                    <span key={clusterId} className="inline-flex items-center gap-1 bg-primary-50 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                        style={{ backgroundColor: cluster?.color || '#9ca3af' }}
                        title={cluster?.name}
                        aria-hidden="true"
                      />
                      {cluster?.name || clusterId}
                      <button type="button" onClick={() => removeCluster(clusterId)} className="hover:text-red-500"><X size={12} /></button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <select
                className="input-field flex-1"
                value=""
                onChange={(e) => { if (e.target.value) { setLastClusterPickId(e.target.value); addCluster(e.target.value); e.target.value = ''; } }}
              >
                <option value="">Cluster auswählen...</option>
                {clusters.filter(c => !formData.clusterIds.includes(c.id)).map(cluster => (
                  <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
                ))}
              </select>
              {(() => {
                const picked = clusters.find(c => c.id === lastClusterPickId);
                return picked && formData.clusterIds.includes(lastClusterPickId) ? (
                  <span
                    className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                    style={{ backgroundColor: picked.color || '#9ca3af' }}
                    title={picked.name}
                    aria-hidden="true"
                  />
                ) : null;
              })()}
            </div>
          </div>

          {/* ─── Zuweisung section ─── */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <Users size={16} /> Zuweisung
            </h3>

            {/* Organisationen zuweisen */}
            <div className="mb-4">
              <label className="label flex items-center gap-1">
                <Building2 size={14} /> Organisationen zuweisen (1 Submission pro OE reicht)
              </label>
              {formData.assigneeOrgIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {formData.assigneeOrgIds.map(orgId => {
                    const org = organizations.find(o => o.id === orgId);
                    return (
                      <span key={orgId} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                        {org?.name || orgId}
                        <button type="button" onClick={() => removeAssigneeOrg(orgId)} className="hover:text-red-500"><X size={12} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <select
                  className="input-field"
                  value=""
                  onChange={(e) => { if (e.target.value) { addAssigneeOrg(e.target.value); e.target.value = ''; }}}
                >
                  <option value="">Organisation auswählen...</option>
                  {organizations.filter(o => !formData.assigneeOrgIds.includes(o.id)).map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Benutzer zuweisen */}
            <div>
              <label className="label flex items-center gap-1">
                <Users size={14} /> Einzelne Mitarbeiter zuweisen (jeder muss ausfüllen)
              </label>
              {formData.assigneeUserIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {formData.assigneeUserIds.map(uid => {
                    const u = users.find((us: any) => us.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                        {u?.displayName || u?.username || uid}
                        <button type="button" onClick={() => removeAssigneeUser(uid)} className="hover:text-red-500"><X size={12} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <select
                  className="input-field"
                  value=""
                  onChange={(e) => { if (e.target.value) { addAssigneeUser(e.target.value); e.target.value = ''; }}}
                >
                  <option value="">Mitarbeiter auswählen...</option>
                  {users.filter((u: any) => !formData.assigneeUserIds.includes(u.id)).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.displayName || u.username || u.email}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Organisationen-Zuweisung: <strong>1 Einreichung pro OE</strong>. Nutzer-Zuweisung: <strong>jeder einzeln</strong>.
                Ist ein Nutzer in einer zugewiesenen Organisation, erfüllt seine Einreichung beide.
              </p>
            </div>
          </div>

          {/* ─── Reminder section ─── */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <Bell size={16} /> Erinnerung
            </h3>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={formData.reminderActive}
                onChange={(e) => setFormData({ ...formData, reminderActive: e.target.checked })}
              />
              <span className="text-sm text-gray-700">Erinnerung aktivieren</span>
            </label>

            {formData.reminderActive && (
              <div className="mt-3">
                <label className="label">Minuten vor Fälligkeit erinnern</label>
                <input
                  type="number"
                  min="1"
                  className="input-field"
                  placeholder="Z. B. 30"
                  value={formData.remindBeforeSeconds}
                  onChange={(e) => setFormData({ ...formData, remindBeforeSeconds: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* ─── Actions ─── */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
            <button type="submit" disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? (
                <><Loader2 size={16} className="animate-spin" /> Speichern...</>
              ) : "Erstellen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function QuestionnairesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [clusterFilter, setClusterFilter] = useState("");
  const [tab, setTab] = useState<'active' | 'deleted'>('active');

  const isDeletedTab = tab === 'deleted';

  const { data: clustersData } = useQuery<Cluster[]>({
    queryKey: ["clusters"],
    queryFn: async () => {
      const res = await api.get("/clusters");
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });
  const clusters = clustersData ?? [];

  const { data: rawData, isLoading, error } = useQuery<PaginatedResponse<Questionnaire>>({
    queryKey: ["questionnaires", { deleted: isDeletedTab }],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Questionnaire>>("/questionnaires", {
        params: { limit: 100, deleted: isDeletedTab ? 'true' : undefined },
      });
      return res.data;
    },
  });
  const data = Array.isArray(rawData) ? rawData : (rawData?.data ?? []);

  const hardDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/questionnaires/${id}?hard=true`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["questionnaires"] }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: QuestionnaireStatus }) => {
      await api.patch(`/questionnaires/${id}`, { status });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["questionnaires"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/questionnaires/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["questionnaires"] }),
  });

  const filtered = data.filter((q) =>
    q.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (!clusterFilter ||
      (clusterFilter === "__none__"
        ? (q.clusters ?? []).length === 0
        : (q.clusters ?? []).some((entry: ClusterAssignment) => entry.clusterId === clusterFilter)))
  );

  const handleBotCreated = () => {
    setShowModal(false);
    queryClient.invalidateQueries({ queryKey: ["questionnaires"] });
  };

  const handleDelete = (q: Questionnaire, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDeletedTab) {
      if (window.confirm(`Fragebogen "${q.name}" ENDGÜLTIG löschen? Alle Daten werden unwiderruflich entfernt!`)) {
        hardDeleteMutation.mutate(q.id);
      }
    } else {
      if (window.confirm(`Fragebogen "${q.name}" wirklich löschen?`)) {
        deleteMutation.mutate(q.id);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Fragebögen</h1>
            <p className="text-sm text-gray-500">Verwaltung von Fragebögen und Einreichungen</p>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <Plus size={18} /> Neuer Fragebogen
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Fragebogen suchen..." className="input-field pl-10" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="relative flex items-center gap-2">
          <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select className="input-field pl-10" value={clusterFilter} onChange={(e) => setClusterFilter(e.target.value)}>
            <option value="">Alle Cluster</option>
            <option value="__none__">Ohne Cluster</option>
            {clusters.map((cluster) => (
              <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
            ))}
          </select>
          {(() => {
            const selected = clusterFilter ? clusters.find((cluster) => cluster.id === clusterFilter) : undefined;
            return selected ? (
              <span
                className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                style={{ backgroundColor: selected.color || '#9ca3af' }}
                title={selected.name}
                aria-hidden="true"
              />
            ) : null;
          })()}
        </div>
      </div>

      {/* Tabs: Aktiv / Gelöschte */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            !isDeletedTab
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Aktiv
        </button>
        <button
          onClick={() => setTab('deleted')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            isDeletedTab
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🗑 Gelöschte
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Fragebögen konnten nicht geladen werden</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full py-12 text-center">
            <ClipboardList size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery || clusterFilter ? "Keine Fragebögen gefunden" : "Noch keine Fragebögen vorhanden"}
            </p>
          </div>
        ) : (
          filtered.map((q) => (
            <div
              key={q.id}
              onClick={() => navigate(`/questionnaires/${q.id}`)}
              className="card cursor-pointer hover:shadow-md hover:border-gray-300 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {(() => {
                      const assigned = (q.clusters ?? [])
                        .map((entry: ClusterAssignment) => clusters.find((cluster: Cluster) => cluster.id === entry.clusterId))
                        .filter((cluster: Cluster | undefined): cluster is Cluster => Boolean(cluster));
                      if (assigned.length === 0) return null;
                      const visible = assigned.slice(0, 3);
                      const remaining = assigned.slice(3);
                      return (
                        <span className="mr-1.5 inline-flex items-center gap-1 align-middle">
                          {visible.map((cluster: Cluster) => (
                            <span
                              key={cluster.id}
                              className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                              style={{ backgroundColor: cluster.color || '#9ca3af' }}
                              title={cluster.name}
                              aria-hidden="true"
                            />
                          ))}
                          {remaining.length > 0 && (
                            <span
                              className="text-xs font-normal text-gray-500"
                              title={remaining.map((cluster: Cluster) => cluster.name).join(', ')}
                            >
                              +{remaining.length}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                    {q.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">v{q.version} · {q.organization?.name ?? "—"}</p>
                </div>
                <div className="flex items-center gap-1">
                  {isDeletedTab ? (
                    <span className="text-xs text-red-400 font-medium px-1.5 py-0.5 rounded bg-red-50">Gelöscht</span>
                  ) : (
                    <select
                      className={`text-xs font-medium px-1.5 py-0.5 rounded border-0 cursor-pointer ${statusColors[q.status as QuestionnaireStatus]}`}
                      value={q.status}
                      onChange={(e) => { e.stopPropagation(); updateStatusMutation.mutate({ id: q.id, status: e.target.value as QuestionnaireStatus }); }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  )}
                  <button onClick={(e) => handleDelete(q, e)} className={`btn-ghost p-1.5 transition-all ${isDeletedTab ? 'text-red-400 hover:text-red-600' : 'text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100'}`} title={isDeletedTab ? 'Endgültig löschen' : 'Löschen'}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {q.description && <p className="text-sm text-gray-600 line-clamp-2 mb-3">{q.description}</p>}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <ClipboardList size={14} />
                <span>{q.fields?.length ?? 0} Felder</span>
                <span>· {q._count?.assignments ?? 0} User · {q._count?.orgAssignments ?? 0} OEs</span>
                <Eye size={14} className="ml-auto" />
                <span className="text-primary-600 group-hover:underline">Details</span>
              </div>
            </div>
          ))
        )}
      </div>

      <QuestionnaireFormModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <AiBotWidget
        page="questionnaires"
        active={showModal}
        onCreated={handleBotCreated}
      />
    </div>
  );
}
